/* eslint-disable check-file/filename-blocklist -- pre-existing in this legacy file; the dark-mode sweep only themes its colour literals */
import { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useField } from 'formik';
import { getCustomStyles } from '@/theme/components/select';
import { createLabelForSelect } from '../../SelectComponents';
import { useCreatableSelectHelpers } from './creatableSelectHelpers';
//use this component for auto-complete and createable-select
export const CustomCreatableSelectField = ({ item, formik = {} }) => {
  const [selected, setSelected] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [optionsL, setOptionsL] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const {
    name = '',
    isMulti = false,
    placeholder = '',
    createPlaceHolder = 'Create',
    menuPlacement = 'auto',
    disabled,
    options = [],
    newStyles = [],
    defaultValue,
    components = {},
    isAppendToBody = false,
    isClearable = false,
    isAutoComplete = false,
    lowercase = false,
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { error, initialValue, value } = meta;
  const { setValue, setTouched, setError } = helpers;

  const { submitCount } = formik || {};
  const showError = submitCount > 0 && error;

  const { formatOptionLabel, isValidNewOption, NoOptionsMessageComp } =
    useCreatableSelectHelpers({
      item,
      setSelected,
      setIsLoading,
    });

  useEffect(() => {
    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      setSelected(null);
    }
  }, [value]);

  useEffect(() => {
    let newOptions;
    if (Array.isArray(initialValue) && initialValue?.length > 0) {
      newOptions = initialValue.map((value) => ({ label: value, value }));
    } else if (initialValue?.length > 0) {
      newOptions = { label: initialValue, value: initialValue };
    }
    setSelected(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (!defaultValue?.id) return;
    setSelected({
      label: defaultValue?.name,
      value: defaultValue?.id,
    });
  }, [defaultValue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (selectedOption) => {
    // Ensure the selected option has the correct structure
    if (isMulti) {
      const normalizedOptions =
        selectedOption?.map((opt) => ({
          label: opt?.label,
          value: opt?.value,
          avatar: opt?.avatar,
        })) || [];

      setSelected(normalizedOptions);
      setValue(normalizedOptions?.map((item) => item.value) || []);
      setInputValue('');
    } else {
      // For single select, directly use the selectedOption
      setSelected(selectedOption);
      setValue(selectedOption?.value || null);
      setInputValue('');
    }

    setTouched(true);
    setError(null);
  };

  const renderStaticOptions = (input) => {
    if (!input) {
      setOptionsL([]);
      return [];
    }
    const sanitizedInput = input?.includes('@')
      ? input?.split('@')[0]?.toLowerCase()
      : input?.toLowerCase();

    // Generate email options with unique IDs to prevent selection issues
    const modifiedOptions = options
      .map((option) => {
        const emailValue = `${sanitizedInput}@${option.label}`;

        // Validate email format before adding to options
        const emailRegex =
          /^[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;
        if (!emailRegex.test(emailValue)) {
          return null;
        }

        // Check if the email is already present in the options
        const emailExists = option.emails && option.emails.includes(emailValue);

        if (emailExists) {
          return null;
        }

        return {
          label: emailValue,
          value: emailValue, // Ensure value is unique and matches label
          avatar: option?.avatar,
          name: option?.name,
        };
      })
      .filter((option) => option !== null); // Filter out invalid emails and duplicates

    setOptionsL(modifiedOptions);
    return modifiedOptions;
  };

  // Custom format option label function to display email parts differently
  const customFormatOptionLabel = (option) => {
    if (!option || !option.label) return '';

    // Check if it's an email format
    if (typeof option?.label === 'string' && option?.label.includes('@')) {
      const [username, domain] = option.label.split('@');
      return (
        <span>
          {lowercase ? username.toLowerCase() : username}
          <span
            style={{
              fontWeight: '551',
              color: 'var(--kula-dark-secondary-900, #413B74)',
            }}
          >
            {lowercase ? `@${domain.toLowerCase()}` : `@${domain}`}
          </span>
        </span>
      );
    }

    // Use the original formatOptionLabel for non-email options
    return lowercase && typeof option.label === 'string'
      ? formatOptionLabel({ ...option, label: option.label.toLowerCase() })
      : formatOptionLabel(option);
  };

  // Handle input change to dynamically update options
  const onInputChange = (newInput, { action }) => {
    // Only update when the user is typing
    if (action === 'input-change') {
      setInputValue(newInput);
      if (isAutoComplete && newInput) {
        renderStaticOptions(newInput);
      }
    }
  };

  // Force update options for autocomplete when options change
  useEffect(() => {
    if (isAutoComplete && inputValue) {
      renderStaticOptions(inputValue);
    }
  }, [options, isAutoComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Custom onCreate handler for email options
  const handleCreateOption = (inputValue) => {
    setIsLoading(true);
    try {
      let newOption;
      const emailRegex =
        /^[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;

      if (isAutoComplete && options.length > 0) {
        // If autocomplete is enabled, create an email-style option
        const sanitizedInput = inputValue.includes('@')
          ? inputValue
          : `${inputValue}@${options[0].label}`;

        // Validate the email format
        if (!emailRegex.test(sanitizedInput)) {
          setError('Invalid email format');
          setIsLoading(false);
          return;
        }

        newOption = {
          label: sanitizedInput,
          value: sanitizedInput,
        };
      } else {
        // Default behavior
        // For emails, still validate even in non-autocomplete mode
        if (inputValue.includes('@') && !emailRegex.test(inputValue)) {
          setError('Invalid email format');
          setIsLoading(false);
          return;
        }

        newOption = {
          label: inputValue,
          value: inputValue,
        };
      }

      // Update the selected value
      if (isMulti) {
        const newSelected = selected ? [...selected, newOption] : [newOption];
        setSelected(newSelected);
        setValue(newSelected?.map((item) => item?.value));
      } else {
        setSelected(newOption);
        setValue(newOption?.value);
      }

      setTouched(true);
      setError(null);

      // Clear input value after creating an option
      setInputValue('');
    } catch (error) {
      console.error('Error creating option:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Manually construct current options - combine static options with dynamic ones
  const allAvailableOptions = isAutoComplete ? [...optionsL] : [...options];

  const baseProps = {
    styles: getCustomStyles(newStyles),
    isMulti: isMulti,
    inputId: name,
    placeholder: isLoading
      ? 'Creating...'
      : placeholder || 'Select or create an option...',
    options: allAvailableOptions,
    hasError: showError,
    components: {
      IndicatorSeparator: null,
      NoOptionsMessage: NoOptionsMessageComp,
      ...components,
    },
    formatOptionLabel: isAutoComplete
      ? customFormatOptionLabel
      : (option) => {
          if (lowercase && option && typeof option.label === 'string') {
            return formatOptionLabel({
              ...option,
              label: option.label.toLowerCase(),
            });
          }
          return formatOptionLabel(option);
        },
    isValidNewOption: isValidNewOption,
    onChange: handleChange,
    onCreateOption: handleCreateOption,
    menuPlacement: menuPlacement,
    value: selected,
    isClearable: isClearable,
    isDisabled: disabled || isLoading,
    isLoading: isLoading,
    formatCreateLabel: (val) =>
      createLabelForSelect(
        lowercase ? val.toLowerCase() : val,
        createPlaceHolder,
        optionsL
      ),
    className: 'kula_custom_select__container',
    classNamePrefix: 'kula_custom_select',
    onInputChange: onInputChange,
    inputValue: lowercase ? inputValue.toLowerCase() : inputValue,
    // Ensure exact matches for selection
    getOptionValue: (option) => option?.value,
    getOptionLabel: (option) =>
      lowercase && typeof option?.label === 'string'
        ? option.label.toLowerCase()
        : option?.label,
  };

  if (isAppendToBody) {
    return <CreatableSelect {...baseProps} menuPortalTarget={document?.body} />;
  }

  return <CreatableSelect {...baseProps} />;
};
